import { generateActionTask, generateScreenTask } from '@utils/flows';
import { merge } from 'lodash';
import {
	HiddenActionInputs,
	IFlowActions,
	IFlowScreens,
	NextActionTaskType,
	POLLING_GROUP_INTERACTION_ID,
	Task,
	VariableType
} from 'types/flows';
import { IFlowSnippet } from '../FlowViewAddTaskDialog';
import enchantedLinkScreens from './Screens/enchantedlink.json';
import passowrdScreens from './Screens/password.json';
import magicLinkScreens from './Screens/magiclink.json';
import otpScreens from './Screens/otp.json';
import totpScreens from './Screens/totp.json';

type TemplateSendMethod = 'email' | 'sms';
type TemplateStartMethod =
	| 'update-user'
	| 'sign-up-or-in'
	| 'sign-up'
	| 'sign-in';

interface IFlowSnippetTemplate
	extends Omit<IFlowSnippet, 'tasks' | 'screens' | 'title' | 'id'> {
	id?: string;
	title?: string;
	sendMethod?: TemplateSendMethod;
	startMethod: TemplateStartMethod;
	func: (
		template: IFlowSnippetTemplate,
		actions?: IFlowActions
	) => IFlowSnippet | undefined;
}

const buildOTPFlow = (
	template: IFlowSnippetTemplate,
	actions?: IFlowActions
): IFlowSnippet | undefined => {
	const startId = `${template.startMethod}-otp-${template.sendMethod}`;
	const endId = `verify-code-otp-${template.sendMethod}`;
	if (actions?.[startId] && actions?.[endId]) {
		const startAction = actions[startId];

		const firstTask: Task = {
			...generateActionTask(startId, startAction),
			id: '1',
			next: {
				rules: [{ taskId: '2', interactionId: NextActionTaskType.success }]
			}
		};
		const screen =
			otpScreens[
				`${
					template.startMethod === 'update-user' ? 'update' : 'sign'
				}-${template.sendMethod!}`
			];
		const secondTask: Task = {
			...generateScreenTask('Verify OTP'),
			id: '2',
			next: {
				rules: [
					{ taskId: '3', interactionId: 'submit' },
					{ taskId: '1', interactionId: 'resend' }
				]
			},
			screenId: screen.id
		};
		const thirdTask: Task = {
			...generateActionTask(endId, actions[endId]),
			id: '3'
		};
		return {
			...template,
			id: `snippet-${startId}`,
			title: startAction.displayName,
			description: startAction.description,
			tasks: [firstTask, secondTask, thirdTask],
			screens: { [screen.id]: screen } as unknown as IFlowScreens
		};
	}
	return undefined;
};

const buildTOTPFlow = (
	template: IFlowSnippetTemplate,
	actions?: IFlowActions
): IFlowSnippet | undefined => {
	// totp does not have sign-up-or-in
	const startMethod = template.startMethod as Exclude<
		TemplateStartMethod,
		'sign-up-or-in'
	>;

	const startId = `${startMethod}-totp`;
	const endId = 'verify-totp-code';
	if (actions?.[startId] && actions?.[endId]) {
		const startAction = actions[startId];

		let firstTask: Task | null = null;
		if (startId !== 'sign-in-totp') {
			// sign-in-totp snippet compound with only a screen and an end-action
			firstTask = {
				...generateActionTask(startId, startAction),
				id: '1',
				next: {
					rules: [{ taskId: '2', interactionId: NextActionTaskType.success }]
				}
			};
		}

		const screen = totpScreens[startMethod];
		const secondTask: Task = {
			...generateScreenTask('Verify TOTP'),
			id: '2',
			next: {
				rules: [{ taskId: '3', interactionId: 'submit' }]
			},
			screenId: screen.id
		};
		const thirdTask: Task = {
			...generateActionTask(endId, actions[endId]),
			id: '3'
		};

		const tasks = firstTask
			? [firstTask, secondTask, thirdTask]
			: [secondTask, thirdTask];

		return {
			...template,
			id: `snippet-${startId}`,
			title: startAction.displayName,
			description: startAction.description,
			tasks,
			screens: { [screen.id]: screen } as unknown as IFlowScreens
		};
	}
	return undefined;
};

const buildMagicLinkFlow = (
	template: IFlowSnippetTemplate,
	actions?: IFlowActions
): IFlowSnippet | undefined => {
	const startId = `${template.startMethod}-magic-link-${template.sendMethod}-group`;
	const endId = 'verify-magic-link';
	if (actions?.[startId] && actions?.[endId]) {
		const startAction = actions[startId];

		const firstTask: Task = merge(generateActionTask(startId, startAction), {
			id: '1',
			arguments: {
				[HiddenActionInputs.verifyStepId]: {
					value: '3',
					type: VariableType.inline
				}
			},
			next: {
				rules: [
					{ taskId: '2', interactionId: `${NextActionTaskType.success}.0` }
				]
			}
		});
		const screen =
			magicLinkScreens[
				`${
					template.startMethod === 'update-user' ? 'update' : 'sign'
				}-${template.sendMethod!}`
			];

		const verifiedScreen = magicLinkScreens.verified;
		const thirdTask: Task = {
			...generateScreenTask('Verified Successfully'),
			id: '4',
			screenId: verifiedScreen.id
		};

		const secondTask: Task = {
			...generateScreenTask('Magic Link Sent'),
			id: '2',
			next: {
				rules: [
					{ taskId: '1', interactionId: 'resend' },
					{ taskId: '4', interactionId: POLLING_GROUP_INTERACTION_ID }
				]
			},
			screenId: screen.id
		};

		return {
			...template,
			id: `snippet-${startId}`,
			title: startAction.displayName,
			description: startAction.description,
			tasks: [firstTask, secondTask, thirdTask],
			screens: {
				[screen.id]: screen,
				[verifiedScreen.id]: verifiedScreen
			} as unknown as IFlowScreens
		};
	}
	return undefined;
};

const buildEnchantedLinkFlow = (
	template: IFlowSnippetTemplate,
	actions?: IFlowActions
): IFlowSnippet | undefined => {
	const startId = `${template.startMethod}-enchanted-link-${template.sendMethod}-group`;
	const endId = 'verify-enchanted-link';
	if (actions?.[startId] && actions?.[endId]) {
		const startAction = actions[startId];

		const firstTask: Task = merge(generateActionTask(startId, startAction), {
			id: '1',
			arguments: {
				[HiddenActionInputs.verifyStepId]: {
					value: '3',
					type: VariableType.inline
				}
			},
			next: {
				rules: [
					{ taskId: '2', interactionId: `${NextActionTaskType.success}.0` },
					{ taskId: '4', interactionId: `${NextActionTaskType.success}.1` }
				]
			}
		});
		// Enchanted link only supports email
		const sentScreen =
			enchantedLinkScreens[
				`${template.startMethod === 'update-user' ? 'update' : 'sign'}-email`
			];

		const secondTask: Task = {
			...generateScreenTask('Enchanted Link Sent'),
			id: '2',
			next: {
				rules: [{ taskId: '1', interactionId: 'resend' }]
			},
			screenId: sentScreen.id
		};

		const verifiedScreen = enchantedLinkScreens.verified;
		const thirdTask: Task = {
			...generateScreenTask('Verified Successfully'),
			id: '4',
			screenId: verifiedScreen.id
		};

		return {
			...template,
			id: `snippet-${startId}`,
			title: startAction.displayName,
			description: startAction.description,
			tasks: [firstTask, secondTask, thirdTask],
			screens: {
				[sentScreen.id]: sentScreen,
				[verifiedScreen.id]: verifiedScreen
			} as unknown as IFlowScreens
		};
	}
	return undefined;
};

const buildResetPasswordFlow = (
	actions?: IFlowActions
): IFlowSnippet | undefined => {
	const startId = `password-send-reset-group`;
	const endId = 'password-update';
	if (actions?.[startId] && actions?.[endId]) {
		const startAction = actions[startId];
		const updateAction = actions[endId];

		const firstTask: Task = merge(generateActionTask(startId, startAction), {
			id: '1',
			arguments: {},
			next: {
				rules: [
					{ taskId: '2', interactionId: `${NextActionTaskType.success}.0` },
					{ taskId: '3', interactionId: `${NextActionTaskType.success}.1` }
				]
			}
		});
		const screen = passowrdScreens['password-reset-sent'];

		const secondTask: Task = {
			...generateScreenTask('Password Reset Sent'),
			id: '2',
			next: {
				rules: [
					{ taskId: '1', interactionId: 'resend' },
					{ taskId: '5', interactionId: POLLING_GROUP_INTERACTION_ID }
				]
			},
			screenId: screen.id
		};

		const verifiedScreen = passowrdScreens.verified;
		const thirdTask: Task = {
			...generateScreenTask('Verified Successfully'),
			id: '3',
			screenId: verifiedScreen.id,
			next: {
				rules: [{ taskId: '4', interactionId: 'reset' }]
			}
		};

		const forthTask: Task = merge(generateActionTask(endId, updateAction), {
			id: '4',
			arguments: {},
			next: {
				rules: []
			}
		});

		const continueScreen = passowrdScreens['continue-update'];

		const fifthTask: Task = {
			...generateScreenTask('Continue Update'),
			id: '5',
			next: {
				rules: []
			},
			screenId: continueScreen.id
		};

		return {
			id: `snippet-${startId}`,
			title: startAction.displayName,
			description: startAction.description,
			tasks: [firstTask, secondTask, fifthTask, thirdTask, forthTask],
			screens: {
				[screen.id]: screen,
				[verifiedScreen.id]: verifiedScreen,
				[continueScreen.id]: continueScreen
			} as unknown as IFlowScreens
		};
	}
	return undefined;
};

const snippetTemplates: IFlowSnippetTemplate[] = [
	{
		sendMethod: 'email',
		startMethod: 'sign-up-or-in',
		func: buildOTPFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-up-or-in',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-up-or-in',
		func: buildEnchantedLinkFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-up-or-in',
		func: buildOTPFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-up-or-in',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-up',
		func: buildOTPFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-up',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-up',
		func: buildEnchantedLinkFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-up',
		func: buildOTPFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-up',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-in',
		func: buildOTPFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-in',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'sign-in',
		func: buildEnchantedLinkFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-in',
		func: buildOTPFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'sign-in',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'update-user',
		func: buildOTPFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'update-user',
		func: buildMagicLinkFlow
	},
	{
		sendMethod: 'email',
		startMethod: 'update-user',
		func: buildEnchantedLinkFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'update-user',
		func: buildOTPFlow
	},
	{
		sendMethod: 'sms',
		startMethod: 'update-user',
		func: buildMagicLinkFlow
	},
	{
		startMethod: 'sign-in',
		func: buildTOTPFlow
	},
	{
		startMethod: 'sign-up',
		func: buildTOTPFlow
	},
	{
		startMethod: 'update-user',
		func: buildTOTPFlow
	}
];

export const getSnippets = (actions?: IFlowActions) => {
	const snippets: IFlowSnippet[] = [];
	for (const template of snippetTemplates) {
		const snippet = template.func(template, actions);
		if (snippet) snippets.push(snippet);
	}
	const resetPasswordSnippet = buildResetPasswordFlow(actions);
	if (resetPasswordSnippet) snippets.push(resetPasswordSnippet);
	return snippets;
};
